import { GoogleGenerativeAI, HarmCategory, HarmBlockThreshold } from "@google/generative-ai";
import { validateCustomScenario, ContentModerationError } from "./moderation";

export class GeminiService {
    constructor(apiKey) {
        this.apiKey = apiKey;
        this.genAI = new GoogleGenerativeAI(apiKey);
        this.safetySettings = [
            {
                category: HarmCategory.HARM_CATEGORY_HARASSMENT,
                threshold: HarmBlockThreshold.BLOCK_LOW_AND_ABOVE,
            },
            {
                category: HarmCategory.HARM_CATEGORY_HATE_SPEECH,
                threshold: HarmBlockThreshold.BLOCK_LOW_AND_ABOVE,
            },
            {
                category: HarmCategory.HARM_CATEGORY_SEXUALLY_EXPLICIT,
                threshold: HarmBlockThreshold.BLOCK_LOW_AND_ABOVE,
            },
            {
                category: HarmCategory.HARM_CATEGORY_DANGEROUS_CONTENT,
                threshold: HarmBlockThreshold.BLOCK_LOW_AND_ABOVE,
            },
        ];
        this.primaryModelName = "gemini-2.5-flash";
        this.model = this.genAI.getGenerativeModel({ model: this.primaryModelName, safetySettings: this.safetySettings });
    }

    async generateExercise(year, scenario, options = {}) {
        if (options.isCustom) {
            const validation = validateCustomScenario(scenario);
            if (!validation.isValid) {
                throw new ContentModerationError(validation.reason, validation.category);
            }
        }

        let prompt = `Agisci come un docente di informatica che deve creare un'esercitazione di laboratorio.\n`;
        prompt += `Genera il testo di un esercizio per una classe ${year}^ di un istituto tecnico ITIS indirizzo informatico.\n`;
        prompt += `Scenario base: ${scenario}\n\n`;

        prompt += `### REQUISITI TECNICI E LINGUAGGIO (Obbligatori):\n`;
        if (year == '3') {
            prompt += `- Linguaggio: C#\n`;
            prompt += `- Tipologia Applicazione: Windows Forms (.NET)\n`;
            prompt += `- Logica: Uso di funzioni/metodi per ogni operazione.\n`;

            if (options.difficulty === 'Facile') {
                prompt += `- Struttura Dati: Usa **ESATTAMENTE 1 singola Struct** (e Array di Struct).\n`;
            } else if (options.difficulty === 'Medio') {
                prompt += `- Struttura Dati: Usa **ESATTAMENTE 2 Struct** collegate tra loro (e Array di Struct).\n`;
            } else if (options.difficulty === 'Difficile') {
                prompt += `- Struttura Dati: Usa **ESATTAMENTE 4 Struct** collegate logicamente (e Array di Struct).\n`;
            } else {
                // Default fallback
                prompt += `- Struttura Dati: Uso di Struct e Array di Struct.\n`;
            }

        } else if (year == '4') {
            prompt += `- Linguaggio: C#\n`;
            prompt += `- Tipologia Applicazione: Windows Forms (.NET)\n`;
            prompt += `- POO: Uso obbligatorio di Classi, Metodi, Incapsulamento.\n`;
            prompt += `- Strutture dati: Liste (List<T>) o strutture dinamiche.\n`;

            if (options.classType === 'Semplici') {
                prompt += `- Focus Classi: Usa **ESATTAMENTE 1 singola Classe**.\n`;
            } else if (options.classType === 'Composte') {
                prompt += `- Focus Classi: Usa **Classi Composte**. Devi usare una Classe che contiene al suo interno un'istanza di un'altra Classe.\n`;
            } else if (options.classType === 'Derivate') {
                prompt += `- Focus Classi: Usa **Ereditarietà e Composizione**. Devi usare una Classe Base, una Classe Derivata, e una classe contenitore.\n`;
            }

        } else if (year == '5') {
            prompt += `- Linguaggio: C# (ASP.NET Core)\n`;
            prompt += `- Tipologia Applicazione: Web App con Razor Pages\n`;
            prompt += `- Database: Integrazione (simulata o reale) con SQL Server.\n`;
            prompt += `- Architettura: Uso di Classi per il modello e separazione della logica.\n`;

            if (options.difficulty === 'Facile') {
                prompt += `- VINCOLO STRUTTURALE RESTRITTIVO: L'intero esercizio deve basarsi SU **ESATTAMENTE 2 CLASSI** (non di più).\n`;
                prompt += `  1. Una **Classe Container/Principale** (es. Biblioteca, Garage, Scuola).\n`;
                prompt += `  2. Una **Classe Contenuta** (es. Libro, Auto, Studente) gestita tramite una \`List<T>\` dentro la principale.\n`;
                prompt += `- NON creare classi per Utenti, Prestiti o altro se superano il limite di 2 classi totali.\n`;

            } else if (options.difficulty === 'Medio') {
                prompt += `- VINCOLO STRUTTURALE: Usa **ESATTAMENTE 4 CLASSI** in totale.\n`;
                prompt += `- Una classe deve contenerne un'altra (es. Composizione/Lista).\n`;
                prompt += `- Le altre due possono essere classi di supporto o collegate.\n`;

            } else if (options.difficulty === 'Difficile') {
                prompt += `- VINCOLO STRUTTURALE: Usa **ESATTAMENTE 4 CLASSI** in totale.\n`;
                prompt += `- Definisci relazioni complesse (es. 1 a molti, composizione) tra di esse.\n`;
            }
        }

        prompt += `\n### STRUTTURA DEL TESTO (Segui rigorosamente questo formato):\n`;
        prompt += `1. **Introduzione**: Descrizione realistica del contesto (es. "Un'azienda ha bisogno di...", "Uno studio medico...").\n`;
        prompt += `2. **Specifiche dei Dati**: Elenco puntato chiaro delle entità e dei campi, specificando tipi e vincoli (es. "prezzo > 0", "codice univoco").\n`;
        prompt += `3. **Funzionalità Richieste**: Scrivi: "L'applicazione dovrà consentire di:".\n`;
        prompt += `   A seguire, un elenco numerato (1, 2, 3...) che DEVE includere:\n`;
        prompt += `   - **Operazioni CRUD**: Inserimento, Modifica (dato un identificativo), Cancellazione (dato un criterio), Visualizzazione elenco.\n`;
        prompt += `   - **4 Funzionalità di Analisi** (in ordine di difficoltà crescente): \n`;
        prompt += `     Richiedi filtri specifici, ordinamenti (es. per data, per importo), e calcoli aggregati (totali, medie, min/max condizionali).\n`;
        prompt += `     Esempio: "Dato un [criterio], visualizzare...", "Calcolare la media di...", "Visualizzare i record che..."\n`;

        prompt += `4. **Nota Conclusiva**: Aggiungi alla fine del documento una linea orizzontale di separazione (\`---\`) seguita da una nota che indica: "*Nota: Questo documento è stato generato interamente dall'Intelligenza Artificiale (AI).*"\n`;

        prompt += `\n### NOTE AGGIUNTIVE:\n`;
        prompt += `- Stile professionale e accademico.\n`;
        prompt += `- NON includere intestazioni come "Data:" o "Tempo a disposizione:".\n`;
        prompt += `- I punti da svolgere devono essere chiari e testabili.\n`;
        prompt += `- NON includere soluzioni o codice, solo il testo del problema.\n`;
        prompt += `- Concludi sempre il testo con la nota che specifica che il documento è stato generato interamente dall'Intelligenza Artificiale (AI). Si consiglia sempre una verifica della traccia da parte dello studente.\n`;
        prompt += `- Output finale formattato in Markdown pulito.\n`;

        if (options.isCustom) {
            prompt += `\n### REGOLE DI MODERAZIONE E SICUREZZA PER MINORI (PG-13 Obbligatorie):\n`;
            prompt += `- Target di riferimento: Studenti minorenni (PG-13 / scuola secondaria superiore).\n`;
            prompt += `- TEMI SEVERAMENTE VIETATI:\n`;
            prompt += `  * Volgarità, turpiloquio o linguaggio offensivo.\n`;
            prompt += `  * Riferimenti a violenza esplicita o grafica.\n`;
            prompt += `  * Contenuti a sfondo sessuale, espliciti o allusivi.\n`;
            prompt += `  * Riferimenti a droghe, sostanze illegali, abuso di alcol o gioco d'azzardo.\n`;
            prompt += `  * Incitamento all'odio, discriminazione, bullismo o autolesionismo.\n`;
            prompt += `- Se lo scenario personalizzato richiesto viola una qualsiasi di queste regole, INTERROMPI immediatamente l'elaborazione. NON generare, completare né parafrasare il testo non conforme. Rispondi ESCLUSIVAMENTE con il testo: "MODERATION_POLICY_VIOLATION".\n`;
        }

        const candidateModels = [this.primaryModelName, "gemini-2.0-flash", "gemini-1.5-pro"].filter((m, i, arr) => arr.indexOf(m) === i);
        let lastError = null;

        for (const modelName of candidateModels) {
            try {
                const currentModel = (modelName === this.primaryModelName)
                    ? this.model
                    : this.genAI.getGenerativeModel({ model: modelName, safetySettings: this.safetySettings });

                const result = await currentModel.generateContent(prompt);
                const response = await result.response;
                let text = response.text();

                if (options.isCustom && text.includes("MODERATION_POLICY_VIOLATION")) {
                    throw new ContentModerationError();
                }

                const hasAiNote = /generat[oa].*(interamente|completamente).*(ai|intelligenza artificiale)|documento.*generato.*(ai|intelligenza artificiale)/i.test(text);
                if (!hasAiNote) {
                    text = text.trim() + "\n\n---\n\n*Nota: Questo documento è stato generato interamente dall'Intelligenza Artificiale (AI).*";
                }

                return text;
            } catch (error) {
                lastError = error;
                if (error instanceof ContentModerationError || error.isModerationViolation) {
                    throw error;
                }
                const errStr = (error && (error.message || error.toString())) || "";
                if (errStr.includes("SAFETY") || errStr.includes("blocked") || errStr.includes("blockReason")) {
                    throw new ContentModerationError();
                }
                // If temporary server error / high demand (503), try next available model
                if (errStr.includes("503") || errStr.includes("demand") || errStr.includes("404")) {
                    console.warn(`[GeminiService] Modello ${modelName} non disponibile (503/404), tentativo con modello di fallback...`);
                    continue;
                }
                console.error("Errore nella generazione:", error);
                throw error;
            }
        }

        console.error("Tutti i modelli disponibili hanno restituito errore:", lastError);
        throw lastError;
    }
}
